import React, { useState, useEffect } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { Pencil, Hash, X, Check, Lock } from 'lucide-react';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/v1`;

export interface EditDocumentNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newNumber: string) => void;
  moduleKey: string; // e.g. 'SALES_INVOICE', 'PURCHASE_INVOICE', 'SALES_ORDER', etc.
  id: string;
  currentNumber: string;
  title?: string;
}

export default function EditDocumentNumberModal({
  isOpen,
  onClose,
  onSuccess,
  moduleKey,
  id,
  currentNumber,
  title = 'Edit Document Number',
}: EditDocumentNumberModalProps) {
  const toast = useToast();
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentNumber) {
      const parts = currentNumber.trim().split('-');
      if (parts.length >= 3) {
        const p = parts.slice(0, -1).join('-') + '-';
        const s = parts[parts.length - 1];
        setPrefix(p);
        setSuffix(s);
      } else {
        const lastIndex = currentNumber.search(/\d+$/);
        if (lastIndex !== -1) {
          setPrefix(currentNumber.substring(0, lastIndex));
          setSuffix(currentNumber.substring(lastIndex));
        } else {
          setPrefix(currentNumber + '-');
          setSuffix('0001');
        }
      }
    }
  }, [currentNumber, isOpen]);

  if (!isOpen) return null;

  const fullNewNumber = `${prefix}${suffix}`;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suffix.trim() || !/^\d+$/.test(suffix.trim())) {
      toast.error('Suffix must contain only numeric digits');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/invoice-settings/update-single-doc-number`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          moduleKey,
          id,
          newNumber: fullNewNumber,
        }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to update document number');
      }

      toast.success(json.message || `Number updated to ${fullNewNumber}`);
      if (onSuccess) onSuccess(fullNewNumber);
      onClose();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Error updating document number');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <Hash className="h-4 w-4" />
            </div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              Current Document Number
            </label>
            <div className="rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm font-medium text-slate-700">
              {currentNumber}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              New Number (Prefix Locked)
            </label>
            <div className="flex rounded-lg border border-slate-300 shadow-xs focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
              {/* Prefix (Locked) */}
              <span className="flex items-center gap-1.5 rounded-l-lg bg-slate-100 px-3 py-2 font-mono text-sm font-semibold text-slate-600 border-r border-slate-200 select-none">
                <Lock className="h-3.5 w-3.5 text-slate-400" />
                {prefix}
              </span>

              {/* Editable Suffix */}
              <input
                type="text"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value.replace(/\D/g, ''))}
                placeholder="0001"
                className="w-full rounded-r-lg bg-white px-3 py-2 font-mono text-sm font-bold text-indigo-700 outline-none placeholder:text-slate-300"
                required
                autoFocus
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Only the numeric suffix digits can be modified. The prefix is locked.
            </p>
          </div>

          {/* Full Preview */}
          <div className="rounded-lg bg-indigo-50/70 border border-indigo-100 p-3">
            <div className="text-xs font-medium text-indigo-600">Full New Number Preview:</div>
            <div className="font-mono text-base font-bold text-indigo-900 mt-0.5">{fullNewNumber}</div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !suffix.trim()}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (
                <>Updating...</>
              ) : (
                <>
                  <Check className="h-4 w-4" /> Save Number
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
